'use client';

import * as React from 'react';

const OVERLAY_SELECTOR = '[role="dialog"],[role="alertdialog"]';

function addedOverlays(records: MutationRecord[]): Element[] {
  const found: Element[] = [];
  for (const record of records) {
    record.addedNodes.forEach((node) => {
      if (node.nodeType !== Node.ELEMENT_NODE) return;
      const element = node as Element;
      if (element.matches(OVERLAY_SELECTOR)) found.push(element);
      found.push(...Array.from(element.querySelectorAll(OVERLAY_SELECTOR)));
    });
  }
  return found;
}

/**
 * The panel itself, anything inside it, and a dialog opened by a trigger in
 * the panel share the panel's lifetime, so they are not a hand-off.
 */
function belongsToPanel(overlay: Element, panel: Element | null): boolean {
  if (!panel) return true;
  if (panel === overlay || panel.contains(overlay) || overlay.contains(panel)) {
    return true;
  }
  const { id } = overlay;
  if (!id) return false;
  return Array.from(panel.querySelectorAll('[aria-controls]')).some(
    (control) =>
      control.getAttribute('aria-controls')?.split(/\s+/).includes(id) ?? false,
  );
}

/** A document, or a shadow root a portal or the trigger lives in. */
export type OverlayRoot = Document | ShadowRoot;

function isDocument(root: OverlayRoot): root is Document {
  return root.nodeType === Node.DOCUMENT_NODE;
}

/** The document plus the shadow root `node` sits in, if any. */
export function overlayRootsOf(node: Node): OverlayRoot[] {
  const root = node.getRootNode();
  const roots: OverlayRoot[] = [];
  if (node.ownerDocument) roots.push(node.ownerDocument);
  if (root.nodeType === Node.DOCUMENT_FRAGMENT_NODE && 'host' in root) {
    roots.push(root as ShadowRoot);
  }
  return roots;
}

/** A document is watched through its body; a shadow root directly. */
function observeRoot(observer: MutationObserver, root: OverlayRoot) {
  const target = isDocument(root) ? root.body : root;
  if (target) observer.observe(target, { childList: true, subtree: true });
}

/** The focused element, looking through open shadow roots. */
export function deepActiveElement(doc: Document): Element | null {
  let active = doc.activeElement;
  while (active?.shadowRoot?.activeElement) {
    active = active.shadowRoot.activeElement;
  }
  return active;
}

export function isFocusLost(doc: Document): boolean {
  const active = deepActiveElement(doc);
  // A shadow host whose tree lost its focused element reports itself.
  return !active || active === doc.body || active.shadowRoot != null;
}

interface OverlayHandOffOptions {
  open: boolean;
  /** Turned off on the editor canvas, where the panel stays open. */
  enabled: boolean;
  /** Last panel element; kept after the panel unmounts. */
  panelRef: React.MutableRefObject<Element | null>;
  triggerRef: React.MutableRefObject<HTMLElement | null>;
  /** Documents and shadow roots where a dialog opened from the panel can appear. */
  roots: () => OverlayRoot[];
  close: () => void;
}

/**
 * Closes the panel when another dialog opens, such as a Modal opened from a
 * panel action, without waiting for that dialog to take focus. When that
 * dialog goes away with focus inside it, focus returns to the trigger
 * instead of the page, since the control that opened it closed with the
 * panel.
 *
 * Returns a ref that holds the dialog while a hand-off is in progress, so the
 * panel can skip its own focus return on close.
 */
export function useOverlayHandOff({
  open,
  enabled,
  panelRef,
  triggerRef,
  roots,
  close,
}: OverlayHandOffOptions): React.MutableRefObject<Element | null> {
  const handOffRef = React.useRef<Element | null>(null);
  const stopWatchRef = React.useRef<(() => void) | null>(null);
  const closeRef = React.useRef(close);
  closeRef.current = close;
  const rootsRef = React.useRef(roots);
  rootsRef.current = roots;

  const stopWatch = React.useCallback(() => {
    stopWatchRef.current?.();
    stopWatchRef.current = null;
  }, []);

  const watchForReturn = React.useCallback(
    (overlay: Element) => {
      stopWatch();
      handOffRef.current = overlay;
      const doc = overlay.ownerDocument;
      let focusInside = overlay.contains(deepActiveElement(doc));
      let timer: ReturnType<typeof setTimeout> | undefined;
      const onFocusIn = (event: FocusEvent) => {
        // The document sees a shadow host; the path starts at the real target.
        const target = (event.composedPath()[0] ?? event.target) as Node | null;
        focusInside = overlay.contains(target);
      };
      const observer = new MutationObserver(() => {
        if (overlay.isConnected) return;
        observer.disconnect();
        doc.removeEventListener('focusin', onFocusIn, true);
        // Runs after the dialog's own focus return, which targets the
        // panel control that opened it and is gone.
        timer = setTimeout(() => {
          stopWatchRef.current = null;
          if (handOffRef.current === overlay) handOffRef.current = null;
          const trigger = triggerRef.current;
          if (!focusInside || !trigger?.isConnected) return;
          if (!isFocusLost(trigger.ownerDocument)) return;
          trigger.focus({ preventScroll: true });
        }, 0);
      });
      overlayRootsOf(overlay).forEach((root) => observeRoot(observer, root));
      doc.addEventListener('focusin', onFocusIn, true);
      stopWatchRef.current = () => {
        observer.disconnect();
        doc.removeEventListener('focusin', onFocusIn, true);
        if (timer !== undefined) clearTimeout(timer);
        if (handOffRef.current === overlay) handOffRef.current = null;
      };
    },
    [stopWatch, triggerRef],
  );

  React.useEffect(() => {
    if (!open || !enabled || typeof MutationObserver === 'undefined') {
      return undefined;
    }
    stopWatch();
    let handedOff = false;
    const handle = (records: MutationRecord[], closing: boolean) => {
      if (handedOff) return;
      const overlay = addedOverlays(records).find(
        (element) =>
          element.isConnected && !belongsToPanel(element, panelRef.current),
      );
      if (!overlay) return;
      handedOff = true;
      watchForReturn(overlay);
      if (!closing) closeRef.current();
    };
    const observers = rootsRef.current().map((root) => {
      const observer = new MutationObserver((records) =>
        handle(records, false),
      );
      observeRoot(observer, root);
      return observer;
    });
    return () => {
      // The panel can close first, e.g. when the new dialog takes focus;
      // pending records still mark the hand-off.
      observers.forEach((observer) => {
        handle(observer.takeRecords(), true);
        observer.disconnect();
      });
    };
  }, [open, enabled, panelRef, stopWatch, watchForReturn]);

  React.useEffect(() => stopWatch, [stopWatch]);

  return handOffRef;
}
